import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Crown, Mail, RefreshCw, Shield, Trash2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import type { TableActionItem } from '@/components/shared';
import { usePersistedSearchParams } from '@/preferences';
import {
  fetchBackofficeOrganizationMembers,
  manageBackofficeOrganizationInvitation,
  removeBackofficeOrganizationMember,
  transferBackofficeOrganizationOwnership,
  updateBackofficeOrganizationMemberRole,
} from '../api';
import type {
  BackofficeOrganizationMemberRow,
  OrganizationMembersSortBy,
  OrganizationManagedMemberRoleLabel,
} from '../types';
import { OrganizationMemberActionModal } from './OrganizationMemberActionModal';
import { OrganizationMembersFilters } from './OrganizationMembersFilters';
import { OrganizationMembersTable } from './OrganizationMembersTable';

const DEFAULT_LIMIT = 20;
const SORTABLE_FIELDS = new Set<OrganizationMembersSortBy>(['name', 'lastSeenOnlineAt', 'addedAt']);

type MemberActionState = {
  kind: 'upgrade' | 'downgrade' | 'transfer' | 'remove' | 'withdraw' | 'resend';
  member: BackofficeOrganizationMemberRow;
  title: string;
  description: string;
  confirmLabel: string;
  successMessage: string;
} | null;

export interface OrganizationMembersSectionProps {
  organizationId: string;
  organizationName: string;
  actions?: TableActionItem[];
  onMemberMutationSuccess?: (message: string) => void;
  surface?: 'page' | 'tile' | undefined;
}

function readSortBy(value: string | null): OrganizationMembersSortBy {
  return value && SORTABLE_FIELDS.has(value as OrganizationMembersSortBy)
    ? (value as OrganizationMembersSortBy)
    : 'lastSeenOnlineAt';
}

function readSortDirection(value: string | null): 'asc' | 'desc' {
  return value === 'asc' || value === 'desc' ? value : 'desc';
}

function buildMemberActionState(
  kind: NonNullable<MemberActionState>['kind'],
  member: BackofficeOrganizationMemberRow,
  organizationName: string
): NonNullable<MemberActionState> {
  const targetLabel = member.name ?? member.email;

  if (kind === 'withdraw' || kind === 'resend') {
    const withdrawing = kind === 'withdraw';
    return {
      kind,
      member,
      title: withdrawing ? 'Withdraw invite' : 'Resend invite',
      description: withdrawing
        ? `Withdraw the invitation for ${targetLabel} to ${organizationName}? Their invitation link will stop working.`
        : `Resend the invitation email to ${targetLabel} for ${organizationName}?`,
      confirmLabel: withdrawing ? 'Withdraw invite' : 'Resend invite',
      successMessage: withdrawing
        ? `Invitation for ${targetLabel} was withdrawn.`
        : `Invitation email queued for ${targetLabel}.`,
    };
  }

  if (kind === 'upgrade') {
    return {
      kind,
      member,
      title: 'Upgrade access',
      description: `Upgrade ${targetLabel} to co-owner in ${organizationName}?`,
      confirmLabel: 'Upgrade',
      successMessage: `${targetLabel} was upgraded to co-owner.`,
    };
  }

  if (kind === 'downgrade') {
    return {
      kind,
      member,
      title: 'Downgrade access',
      description: `Downgrade ${targetLabel} to member in ${organizationName}?`,
      confirmLabel: 'Downgrade',
      successMessage: `${targetLabel} was downgraded to member.`,
    };
  }

  if (kind === 'transfer') {
    return {
      kind,
      member,
      title: 'Transfer ownership',
      description: `Make ${targetLabel} the owner of ${organizationName}?`,
      confirmLabel: 'Make owner',
      successMessage: `${targetLabel} is now the organization owner.`,
    };
  }

  return {
    kind,
    member,
    title: 'Remove member',
    description: `Remove ${targetLabel} from ${organizationName}?`,
    confirmLabel: 'Remove member',
    successMessage: `${targetLabel} was removed from the organization.`,
  };
}

export function OrganizationMembersSection({
  organizationId,
  organizationName,
  actions = [],
  onMemberMutationSuccess,
  surface = 'page',
}: OrganizationMembersSectionProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [members, setMembers] = useState<BackofficeOrganizationMemberRow[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [actionState, setActionState] = useState<MemberActionState>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [now, setNow] = useState(Date.now);
  const loadingMoreRef = useRef(false);
  const refreshAbortControllerRef = useRef<AbortController | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const lastSeenFrom = searchParams.get('memberLastSeenFrom') ?? '';
  const lastSeenTo = searchParams.get('memberLastSeenTo') ?? '';
  const addedFrom = searchParams.get('memberAddedFrom') ?? '';
  const addedTo = searchParams.get('memberAddedTo') ?? '';
  const sortBy = readSortBy(searchParams.get('memberSortBy'));
  const sortDirection = readSortDirection(searchParams.get('memberSortDirection'));

  const requestParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set('sortBy', sortBy);
    params.set('sortDirection', sortDirection);
    if (lastSeenFrom) params.set('lastSeenFrom', lastSeenFrom);
    if (lastSeenTo) params.set('lastSeenTo', lastSeenTo);
    if (addedFrom) params.set('addedFrom', addedFrom);
    if (addedTo) params.set('addedTo', addedTo);
    return params;
  }, [addedFrom, addedTo, lastSeenFrom, lastSeenTo, sortBy, sortDirection]);
  const hasMore = members.length < total;

  useEffect(() => {
    if (!members.some((member) => member.resendAvailableAt)) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [members]);

  const loadMembers = useCallback(
    async (offset: number, signal: AbortSignal) => {
      const params = new URLSearchParams(requestParams);
      params.set('limit', String(DEFAULT_LIMIT));
      params.set('offset', String(offset));
      const response = await fetchBackofficeOrganizationMembers(organizationId, params, signal);
      setTotal(response.page.total);
      setMembers((current) =>
        offset === 0 ? response.members : [...current, ...response.members]
      );
    },
    [organizationId, requestParams]
  );

  const handleRefresh = useCallback(async () => {
    refreshAbortControllerRef.current?.abort();
    const controller = new AbortController();
    refreshAbortControllerRef.current = controller;

    setRefreshing(true);
    setError(null);
    setLoadMoreError(null);

    try {
      await loadMembers(0, controller.signal);
    } catch (caught: unknown) {
      if (caught instanceof DOMException && caught.name === 'AbortError') {
        return;
      }
      setMembers([]);
      setTotal(0);
      setError(caught instanceof Error ? caught.message : 'Failed to refresh organization members');
    } finally {
      if (refreshAbortControllerRef.current === controller) {
        refreshAbortControllerRef.current = null;
      }
      if (!controller.signal.aborted) {
        setRefreshing(false);
      }
    }
  }, [loadMembers]);

  useEffect(() => {
    const controller = new AbortController();
    setInitialLoading(true);
    setError(null);
    setLoadMoreError(null);

    loadMembers(0, controller.signal)
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') {
          return;
        }
        setMembers([]);
        setTotal(0);
        setError(caught instanceof Error ? caught.message : 'Failed to load organization members');
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setInitialLoading(false);
        }
      });

    return () => controller.abort();
  }, [loadMembers]);

  useEffect(() => {
    return () => refreshAbortControllerRef.current?.abort();
  }, []);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || initialLoading || !hasMore) {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    const controller = new AbortController();
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || loadingMoreRef.current) {
          return;
        }

        observer.disconnect();
        loadingMoreRef.current = true;
        setLoadingMore(true);
        setLoadMoreError(null);
        loadMembers(members.length, controller.signal)
          .catch((caught: unknown) => {
            if (caught instanceof DOMException && caught.name === 'AbortError') {
              return;
            }
            setLoadMoreError(
              caught instanceof Error ? caught.message : 'Failed to load more organization members'
            );
          })
          .finally(() => {
            loadingMoreRef.current = false;
            if (!controller.signal.aborted) {
              setLoadingMore(false);
            }
          });
      },
      { rootMargin: '280px' }
    );

    observer.observe(target);

    return () => {
      controller.abort();
      observer.disconnect();
    };
  }, [hasMore, initialLoading, loadMembers, members.length]);

  const tableActions = useMemo<TableActionItem[]>(
    () => [
      ...actions,
      {
        id: 'refresh',
        label: 'Refresh',
        loadingLabel: 'Refreshing',
        icon: RefreshCw,
        onSelect: () => {
          void handleRefresh();
        },
        placement: 'fixed-inline',
        disabled: initialLoading,
        isLoading: refreshing,
      },
    ],
    [actions, handleRefresh, initialLoading, refreshing]
  );

  usePersistedSearchParams({
    scope: `list:organization:${organizationId}:members`,
    keys: [
      'memberLastSeenFrom',
      'memberLastSeenTo',
      'memberAddedFrom',
      'memberAddedTo',
      'memberSortBy',
      'memberSortDirection',
    ],
    searchParams,
    setSearchParams,
  });

  function updateSearchParams(updates: Record<string, string | null>) {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [key, value] of Object.entries(updates)) {
          if (!value) {
            next.delete(key);
          } else {
            next.set(key, value);
          }
        }
        return next;
      },
      { replace: true }
    );
  }

  function handleSortChange(nextSortBy: OrganizationMembersSortBy) {
    const nextDirection = sortBy === nextSortBy && sortDirection === 'asc' ? 'desc' : 'asc';
    updateSearchParams({
      memberSortBy: nextSortBy,
      memberSortDirection: nextDirection,
    });
  }

  function handleRoleChange(
    member: BackofficeOrganizationMemberRow,
    roleLabel: OrganizationManagedMemberRoleLabel
  ) {
    setActionError(null);
    setActionState(
      buildMemberActionState(
        roleLabel === 'Co-owner' ? 'upgrade' : 'downgrade',
        member,
        organizationName
      )
    );
  }

  function getActions(member: BackofficeOrganizationMemberRow): TableActionItem[] {
    if (member.kind === 'invitation') {
      const remainingMinutes = member.resendAvailableAt
        ? Math.max(0, Math.ceil((Date.parse(member.resendAvailableAt) - now) / 60_000))
        : 0;
      return [
        {
          id: `resend-${member.id}`,
          label: member.resendDisabledReason
            ? `Resend invite (${member.resendDisabledReason.toLowerCase()})`
            : remainingMinutes > 0
              ? `Resend invite (available in ${remainingMinutes} min)`
              : 'Resend invite',
          icon: Mail,
          disabled:
            actionSubmitting || Boolean(member.resendDisabledReason) || remainingMinutes > 0,
          onSelect: () => {
            setActionError(null);
            setActionState(buildMemberActionState('resend', member, organizationName));
          },
        },
        {
          id: `withdraw-${member.id}`,
          label: 'Withdraw invite',
          icon: Trash2,
          disabled: actionSubmitting,
          onSelect: () => {
            setActionError(null);
            setActionState(buildMemberActionState('withdraw', member, organizationName));
          },
        },
      ];
    }
    if (member.kind !== 'member' || !member.subjectUserId) {
      return [];
    }
    if (member.roleLabel === 'Owner') {
      return [];
    }
    if (member.roleLabel === 'Co-owner') {
      return [
        {
          id: `downgrade-${member.id}`,
          label: 'Downgrade to member',
          icon: Shield,
          onSelect: () => handleRoleChange(member, 'Member'),
        },
        {
          id: `make-owner-${member.id}`,
          label: 'Make owner',
          icon: Crown,
          onSelect: () => {
            setActionError(null);
            setActionState(buildMemberActionState('transfer', member, organizationName));
          },
        },
        {
          id: `remove-${member.id}`,
          label: 'Remove member',
          icon: Trash2,
          onSelect: () => {
            setActionError(null);
            setActionState(buildMemberActionState('remove', member, organizationName));
          },
        },
      ];
    }

    return [
      {
        id: `upgrade-${member.id}`,
        label: 'Upgrade to co-owner',
        icon: Shield,
        onSelect: () => handleRoleChange(member, 'Co-owner'),
      },
      {
        id: `remove-${member.id}`,
        label: 'Remove member',
        icon: Trash2,
        onSelect: () => {
          setActionError(null);
          setActionState(buildMemberActionState('remove', member, organizationName));
        },
      },
    ];
  }

  async function handleConfirmAction() {
    if (!actionState || actionSubmitting) {
      return;
    }
    const invitationAction = actionState.kind === 'resend' || actionState.kind === 'withdraw';
    if (!invitationAction && !actionState.member.subjectUserId) return;

    setActionSubmitting(true);
    setActionError(null);

    try {
      if (actionState.kind === 'resend' || actionState.kind === 'withdraw') {
        await manageBackofficeOrganizationInvitation(
          organizationId,
          actionState.member.id,
          actionState.kind
        );
      } else if (actionState.kind === 'upgrade') {
        await updateBackofficeOrganizationMemberRole(
          organizationId,
          actionState.member.subjectUserId!,
          {
            roleLabel: 'Co-owner',
          }
        );
      } else if (actionState.kind === 'downgrade') {
        await updateBackofficeOrganizationMemberRole(
          organizationId,
          actionState.member.subjectUserId!,
          {
            roleLabel: 'Member',
          }
        );
      } else if (actionState.kind === 'transfer') {
        await transferBackofficeOrganizationOwnership(
          organizationId,
          actionState.member.subjectUserId!
        );
      } else {
        await removeBackofficeOrganizationMember(organizationId, actionState.member.subjectUserId!);
      }

      const successMessage = actionState.successMessage;
      setActionState(null);
      onMemberMutationSuccess?.(successMessage);
      await handleRefresh();
    } catch (caught: unknown) {
      setActionError(
        caught instanceof Error ? caught.message : 'Failed to update organization member'
      );
    } finally {
      setActionSubmitting(false);
    }
  }

  if (initialLoading) {
    return (
      <section
        className={
          surface === 'tile' ? 'h-full bg-white' : 'rounded-xl border border-neutral-200 bg-white'
        }
      >
        <div className="px-5 py-16 text-center text-body text-neutral-500">
          Loading organization members
        </div>
      </section>
    );
  }

  return (
    <>
      <section
        className={
          surface === 'tile'
            ? 'flex h-full min-h-full flex-col overflow-hidden bg-white'
            : 'rounded-xl border border-neutral-200 bg-white'
        }
      >
        <OrganizationMembersFilters
          lastSeenFrom={lastSeenFrom}
          lastSeenTo={lastSeenTo}
          addedFrom={addedFrom}
          addedTo={addedTo}
          actions={tableActions}
          onLastSeenRangeChange={(from, to) =>
            updateSearchParams({
              memberLastSeenFrom: from,
              memberLastSeenTo: to,
            })
          }
          onAddedRangeChange={(from, to) =>
            updateSearchParams({
              memberAddedFrom: from,
              memberAddedTo: to,
            })
          }
        />

        {error ? (
          <div className="px-5 py-16 text-center text-body text-red-700">{error}</div>
        ) : members.length > 0 ? (
          <div className={surface === 'tile' ? 'min-h-0 flex-1 overflow-y-auto' : undefined}>
            <OrganizationMembersTable
              members={members}
              sortBy={sortBy}
              sortDirection={sortDirection}
              getActions={getActions}
              onSortChange={handleSortChange}
            />
            <div ref={loadMoreRef} className="border-t border-neutral-200 px-5 py-4 text-center">
              {loadingMore ? (
                <span className="text-small text-neutral-500">Loading more members</span>
              ) : loadMoreError ? (
                <span className="text-small text-red-700">{loadMoreError}</span>
              ) : hasMore ? (
                <span className="text-small text-neutral-500">Scroll to load more</span>
              ) : (
                <span className="text-small text-neutral-500">
                  Showing {members.length} of {total}
                </span>
              )}
            </div>
          </div>
        ) : (
          <div className="px-5 py-16 text-center text-body text-neutral-500">
            No organization members found
          </div>
        )}
      </section>

      {actionState ? (
        <OrganizationMemberActionModal
          title={actionState.title}
          description={actionState.description}
          confirmLabel={actionState.confirmLabel}
          submitting={actionSubmitting}
          error={actionError}
          onClose={() => {
            if (!actionSubmitting) {
              setActionError(null);
              setActionState(null);
            }
          }}
          onConfirm={handleConfirmAction}
        />
      ) : null}
    </>
  );
}
